import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });

    // The full-bleed banner is its OWN section, a SIBLING of `main` where the
    // variant picker lives. The variant:update event bubbles to `document` but never
    // through the banner's section, so the section-scoped listener above never fires
    // for it. Add a document-level listener ONLY for the banner. #handleVariantUpdate
    // matches strictly by id, so this is inert for every other gallery's event.
    if (this.classList.contains('media-gallery--full-bleed')) {
      document.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    }

    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    // Ignore duplicate dispatches that reach an already-replaced (detached) node —
    // guards against a gallery hearing the event on both its section and document.
    if (!this.isConnected) return;

    // ca-scent-picker paints this gallery optimistically from its warmed section document
    // the instant a pill is clicked, stamping the variant it painted. The section response
    // is `no-store`, so the fetch driving THIS event could never have been a cache hit — it
    // arrives ~500ms later carrying markup we already rendered. Rebuilding on top of that is
    // pure churn (fresh <img> nodes, another decode, a visible flicker), so skip it when the
    // stamp matches. The stamp is single-use: clear it so any LATER update still applies.
    const optimisticFor = this.dataset.caOptimistic;
    if (optimisticFor && optimisticFor === String(event.detail.resource?.id ?? '')) {
      delete this.dataset.caOptimistic;
      return;
    }

    const source = event.detail.data.html;

    if (!source) return;

    // Match this gallery's counterpart in the re-fetched document by id, so pages
    // with more than one <media-gallery> (e.g. the full-bleed banner + the
    // in-column gallery) each swap their OWN element. querySelector works whether
    // `source` is a Document, Fragment, or Element (getElementById does not).
    const matchById = this.id ? source.querySelector(`#${CSS.escape(this.id)}`) : null;

    // The banner must only ever swap its OWN id — never fall back to first-match, or
    // (e.g. a combined-listing child without a banner) it could wrongly absorb the
    // in-column gallery's markup. Single-gallery pages keep the first-match fallback.
    const allowFirstMatchFallback = !this.classList.contains('media-gallery--full-bleed');
    const newMediaGallery = matchById || (allowFirstMatchFallback ? source.querySelector('media-gallery') : null);

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
